import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/shared/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import Breadcrumb from '@/shared/components/Breadcrumb';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Bell, Wifi, WifiOff, Clock, AlertTriangle,
  Filter, RotateCcw, Layers, Radio, CheckCircle2, ExternalLink, Check, Undo2
} from 'lucide-react';
import { toast } from '@/shared/ui/sonner';
import { useAuth } from '@/shared/auth/AuthContext';
import useBulkSelection from '@/shared/hooks/useBulkSelection';
import useBulkAction from '@/shared/hooks/useBulkAction';
import BulkEditDialog from '@/shared/components/BulkEditDialog';
import BulkResultDialog from '@/shared/components/BulkResultDialog';
import ALERT_BULK_ACTIONS from '@/features/alerts/bulkActions';
import { bulkMarkAlertsRead, bulkMarkAlertsUnread } from '@/shared/api/bulk';

const PRIORITY_CONFIG = {
  critique: { label: 'Critique', class: 'bg-red-100 text-red-700 border-red-200', bg: 'bg-red-100', icon: AlertTriangle, iconColor: 'text-red-500' },
  avertissement: { label: 'Avertissement', class: 'bg-amber-100 text-amber-700 border-amber-200', bg: 'bg-amber-100', icon: AlertTriangle, iconColor: 'text-amber-500' },
  // ⚠️ LE VERT EST CE QUI REND UNE POSITIVE LISIBLE SANS LA RENDRE ALARMANTE. Elle était
  // en ardoise, comme une ligne neutre, et surtout elle n'arrivait jamais ici : le
  // serveur l'écrivait en `info` et la page masquait `info`. Le vert la distingue d'un
  // coup d'œil des deux couleurs qui, elles, appellent un geste.
  positif: { label: 'Bonne nouvelle', class: 'bg-emerald-100 text-emerald-700 border-emerald-200', bg: 'bg-emerald-100', icon: CheckCircle2, iconColor: 'text-emerald-500' },
  // Nom d'avant le 11/09, garde pour les documents deja rendus sous cette priorite.
  information: { label: 'Bonne nouvelle', class: 'bg-emerald-100 text-emerald-700 border-emerald-200', bg: 'bg-emerald-100', icon: CheckCircle2, iconColor: 'text-emerald-500' },
};

/**
 * Les alertes calculées à la volée portent `priority`, celles écrites en base portent
 * `severity` (cf. `core/alerts.py`). Sans cette traduction, une alerte enregistrée
 * tombait dans le repli « avertissement » et un retour à la normale s'affichait en ambre.
 *
 * Les quatre valeurs de gauche d'avant la migration du 11/09 restent lues : un document
 * ancien ne doit pas changer de couleur parce que le vocabulaire a change.
 */
const SEVERITY_TO_PRIORITY = {
  critical: 'critique', critique: 'critique',
  warning: 'avertissement', avertissement: 'avertissement',
  info: 'positif', positif: 'positif', information: 'positif',
};

const prioriteDe = (alert) =>
  alert.priority || SEVERITY_TO_PRIORITY[alert.severity] || 'avertissement';

const TYPE_CONFIG = {
  deconnexion: { label: 'Deconnexion', icon: WifiOff, color: 'text-red-500' },
  // La famille « echeance engagement » a ete retiree le 28/08 (item B7), avec la date
  // de fin d'engagement qui n'aurait jamais du exister. Le serveur ne produit plus ce
  // type : garder le filtre afficherait un onglet qui ne peut plus rien remonter.
  // Batterie qui cesse de répondre pendant qu'on lui maintient une consigne : c'est
  // l'alerte qui manquait le 28/07, quand 240 ordres sont partis dans le vide.
  batterie_injoignable: { label: 'Batterie injoignable', icon: WifiOff, color: 'text-red-500' },
  batterie_reconnectee: { label: 'Batterie de nouveau joignable', icon: Wifi, color: 'text-emerald-500' },
  // Les trois evenements que Boubacar a retenus le 10/09 comme devant « remonter dans
  // les alertes en positif ». Le quatrieme propose — le premier revenu verse — a ete
  // ecarte par lui, et n'est donc pas ici.
  materiel_revenu_en_ligne: { label: 'Materiel revenu en ligne', icon: Wifi, color: 'text-emerald-500' },
  onboarding_qr_success: { label: 'Nouveau chantier installe', icon: CheckCircle2, color: 'text-emerald-500' },
  mandat_signe: { label: 'Nouvelle signature de mandat', icon: CheckCircle2, color: 'text-emerald-500' },
  // Ce que trois ecrans ne disent plus depuis le 11/09, remonte ici (lots A et C).
  actifs_sans_mandat: { label: 'Actifs sans mandat signe', icon: AlertTriangle, color: 'text-amber-500' },
  chantier_attend_materiel: { label: 'Chantier en attente de materiel', icon: Clock, color: 'text-amber-500' },
};

// L'instant d'une alerte, en millisecondes, pour le tri « plus récentes d'abord ». Une
// date illisible ou absente vaut « infiniment vieux » et descend en bas de la liste,
// comme `_anciennete` côté serveur : elle ne remonte jamais au-dessus d'un vrai événement.
function horodatage(alerte) {
  const t = Date.parse(alerte?.created_at || '');
  return Number.isNaN(t) ? -Infinity : t;
}

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // L'ORDRE DE LA LISTE, AU CHOIX. Retour de David en vidéo le 24/09 (« dans l'ordre
  // chronologique, avec la date et l'heure »), arbitré par Boubacar le 26/09 : « par
  // chronologie et gravité ». Par défaut les plus récentes d'abord ; « par gravité »
  // rend l'ordre du serveur (`alerts/routes._cle_tri` : gravité, puis fraîcheur). Le
  // filtre de gravité ci-dessous reste disponible dans les deux ordres.
  const [tri, setTri] = useState('recentes');
  const [acquittement, setAcquittement] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [detection, setDetection] = useState(false);

  /**
   * Cherche les équipements muets et ouvre une alerte INTERNE pour chacun.
   *
   * Le bouton disait « Détecter anomalies + notifier clients », et il écrivait
   * réellement au client final. Revue du 31/08 : « on cherche à avoir une application
   * qui nous permet À NOUS de faire de la flexibilité. Pas besoin de notifier le
   * client, c'est surtout pour nous et notre gestionnaire, ces alertes sur nos
   * devices. » L'envoi au client est débranché ; la détection reste.
   *
   * Les trois nombres restent distincts : trente équipements muets dont aucun ne
   * produit d'alerte parce qu'elles existent toutes déjà n'est PAS une détection qui
   * n'a rien trouvé.
   */
  const lancerDetection = async () => {
    setDetection(true);
    try {
      const r = await api.post('/admin/anomalies/detect');
      const { detectes = 0, signales = 0, deja_signales: dejaSignales = 0 } = r.data || {};
      const pluriel = (n) => (n > 1 ? 's' : '');
      toast.success(
        `${detectes} equipement${pluriel(detectes)} muet${pluriel(detectes)} detecte${pluriel(detectes)}. ` +
        `${signales} alerte${pluriel(signales)} ouverte${pluriel(signales)}.` +
        (dejaSignales > 0
          ? ` ${dejaSignales} deja signale${pluriel(dejaSignales)}.`
          : '')
      );
      fetchAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'La detection a echoue');
    }
    setDetection(false);
  };

  const fetchAlerts = () => {
    api.get('/alerts').then(res => setAlerts(res.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAlerts(); }, []);

  // « batteries » regroupe les deux faces d'un même événement, la perte de contact et le
  // retour : les suivre séparément obligerait à basculer d'un filtre à l'autre.
  // Deux axes de filtrage qui ne se recouvrent pas : le TYPE d'evenement, et la GRAVITE.
  // Boubacar, le 10/09 : « il y a les critiques, avertissement, il faut aussi des alertes
  // positives ». Trois gravites a l'ecran n'ont d'interet que si on peut les isoler --
  // notamment masquer les bonnes nouvelles quand on cherche ce qui va mal.
  const filtres = (
    filter === 'all' ? alerts
      : filter === 'batteries' ? alerts.filter(a => (a.type || '').startsWith('batterie_'))
        : PRIORITY_CONFIG[filter] ? alerts.filter(a => prioriteDe(a) === filter)
          : alerts.filter(a => a.type === filter)
  );
  const filtered = tri === 'recentes'
    ? [...filtres].sort((a, b) => horodatage(b) - horodatage(a))
    : filtres;

  // UN CLIC POUR ACQUITTER UNE ALERTE. Retour de Boubacar du 26/09 : cocher la case,
  // ouvrir « Édition groupée », choisir l'action et confirmer faisait quatre gestes pour
  // un seul. La pastille passe par la MÊME route que l'action groupée, avec une seule
  // alerte : un seul chemin d'écriture, donc une seule trace d'audit.
  const basculerLu = async (alerte) => {
    setAcquittement(alerte.id);
    try {
      await (alerte.lu ? bulkMarkAlertsUnread : bulkMarkAlertsRead)([alerte.id]);
      setAlerts(prev => prev.map(a => (a.id === alerte.id ? { ...a, lu: !alerte.lu } : a)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "L'alerte n'a pas pu être mise à jour");
    }
    setAcquittement(null);
  };

  // Les alertes sont recalculées à chaque appel — leur seul état persistant est
  // l'acquittement (`lu`), mémorisé côté API dans `alerts_read`.
  const bulkSel = useBulkSelection(filtered);
  const bulk = useBulkAction(() => { fetchAlerts(); bulkSel.clear(); });
  const alertLabel = (id) => alerts.find(a => a.id === id)?.message || id;

  const runBulk = async (code, value, def) => {
    setBulkOpen(false);
    await bulk.run(() => def.run(bulkSel.ids, value), {
      alwaysReport: def.alwaysReport,
      success: r => `${r.ok} alerte${r.ok > 1 ? 's' : ''} traitée${r.ok > 1 ? 's' : ''}`,
    });
  };
  const critiques = alerts.filter(a => prioriteDe(a) === 'critique').length;
  const avertissements = alerts.filter(a => prioriteDe(a) === 'avertissement').length;
  // ⚠️ TROIS COMPTEURS, PAS DEUX. Le troisieme carte affichait le TOTAL, ce qui ne dit
  // rien de plus que la ligne « N alertes » juste au-dessus. Depuis le 11/09 il compte
  // les bonnes nouvelles, qui n'existaient tout simplement pas avant : le serveur les
  // ecrivait en `info` et la page masquait `info`.
  const positives = alerts.filter(a => prioriteDe(a) === 'positif').length;

  const formatDateHeure = (iso) => {
    const d = iso ? new Date(iso) : null;
    if (!d || Number.isNaN(d.getTime())) return null;
    return `le ${d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}`
      + ` à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return '-'; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <Breadcrumb items={[{ label: 'Alertes' }]} />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Alertes
          </h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="alerts-results-count">
            {filtered.length} alerte{filtered.length > 1 ? 's' : ''}{filter !== 'all' ? ' (filtrées)' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && bulkSel.count > 0 && (
            <Button size="sm" onClick={() => setBulkOpen(true)} data-testid="open-bulk-btn">
              <Layers className="w-4 h-4 mr-1" /> Édition groupée ({bulkSel.count})
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={lancerDetection}
              disabled={detection}
              data-testid="run-anomaly-detection-btn"
              className="gap-2"
            >
              <Radio className={`w-4 h-4 ${detection ? 'animate-pulse' : ''}`} />
              {detection ? 'Analyse en cours…' : 'Détecter les équipements muets'}
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-700" data-testid="alerts-critique-count">{critiques}</p>
              <p className="text-xs text-red-500">Critiques</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700" data-testid="alerts-avertissement-count">{avertissements}</p>
              <p className="text-xs text-amber-500">Avertissements</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700" data-testid="alerts-positif-count">{positives}</p>
              <p className="text-xs text-emerald-600">Bonnes nouvelles</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 items-center flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        {[
          { key: 'all', label: 'Toutes' },
          { key: 'critique', label: 'Critiques' },
          { key: 'avertissement', label: 'Avertissements' },
          { key: 'positif', label: 'Bonnes nouvelles' },
          { key: 'deconnexion', label: 'Deconnexions' },
          { key: 'batteries', label: 'Batteries' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f.key
                ? 'bg-neutral-900 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
            data-testid={`filter-alerts-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 text-xs" data-testid="tri-alertes">
          <span className="text-slate-400 mr-1">Trier</span>
          {[
            { key: 'recentes', label: 'Plus récentes' },
            { key: 'gravite', label: 'Par gravité' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTri(t.key)}
              className={`px-3 py-1.5 rounded-full font-medium transition-colors ${
                tri === t.key
                  ? 'bg-neutral-900 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`tri-alertes-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {filter !== 'all' && (
          <Button variant="ghost" size="sm" onClick={() => setFilter('all')} data-testid="reset-filters-alerts-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Réinitialiser
          </Button>
        )}
      </div>

      {/* Alert List */}
      {filtered.length === 0 ? (
        <Card className="border-slate-200 border-dashed">
          <CardContent className="py-12 text-center text-slate-400">
            <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p>Aucune alerte active</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {isAdmin && (
            <div className="flex items-center gap-3 px-3 py-2 text-[11px] uppercase tracking-wider text-slate-400 font-medium">
              <Checkbox
                checked={bulkSel.allSelected}
                onCheckedChange={bulkSel.toggleAll}
                data-testid="select-all-alerts"
              />
              <span>Tout sélectionner</span>
              {bulkSel.count > 0 && (
                <span className="normal-case tracking-normal text-slate-500">
                  · {bulkSel.count} choisie{bulkSel.count > 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
          {filtered.map(alert => {
            const pConfig = PRIORITY_CONFIG[prioriteDe(alert)] || PRIORITY_CONFIG.avertissement;
            const tConfig = TYPE_CONFIG[alert.type] || TYPE_CONFIG.deconnexion;
            const TypeIcon = tConfig.icon;
            // Une alerte de fond (batterie injoignable) peut n'être rattachée à aucun
            // client : la carte n'est alors pas cliquable, plutôt que de mener à /clients/null.
            const ouvrable = !!alert.client_id;
            // ⚠️ LA CARTE NE NAVIGUE PLUS AU CLIC. Décision du 26/09 : deux pastilles
            // explicites, « Marquer lu » et « Ouvrir la fiche ». Une carte cliquable à
            // côté d'une pastille ferait partir vers la fiche client sur un clic qui
            // visait la pastille d'à côté.
            return (
              <Card
                key={alert.id}
                className={`border-slate-200 transition-all ${alert.lu ? 'opacity-60' : ''} ${
                  bulkSel.isSelected(alert.id) ? 'ring-1 ring-neutral-400' : ''
                }`}
                data-testid={`alert-card-${alert.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {isAdmin && (
                      <span onClick={e => e.stopPropagation()} className="flex items-center">
                        <Checkbox
                          checked={bulkSel.isSelected(alert.id)}
                          onCheckedChange={() => bulkSel.toggle(alert.id)}
                          data-testid={`select-alert-${alert.id}`}
                        />
                      </span>
                    )}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${pConfig.bg}`}>
                      <TypeIcon className={`w-5 h-5 ${tConfig.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-slate-900">{alert.message}</p>
                        <Badge variant="outline" className={`text-[10px] ${pConfig.class}`}>
                          {pConfig.label}
                        </Badge>
                        {alert.lu && (
                          <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 border-slate-200">
                            Lue
                          </Badge>
                        )}
                      </div>
                      {alert.client_nom && (
                        <p className="text-xs text-slate-500">
                          {alert.client_nom}
                          {alert.client_ville ? ` - ${alert.client_ville}` : ''}
                        </p>
                      )}
                      {/* LA DATE ET L'HEURE SUR CHAQUE ALERTE, retour de David du 24/09. Elle
                          n'était écrite que pour les deux alertes batterie ; les autres
                          portaient `created_at` sans que l'écran le montre. */}
                      {formatDateHeure(alert.created_at) && (
                        <p className="text-[11px] text-slate-500 mt-0.5" data-testid={`alert-date-${alert.id}`}>
                          <Clock className="inline w-3 h-3 mr-1 -mt-0.5" />
                          {tConfig.label} · {formatDateHeure(alert.created_at)}
                        </p>
                      )}
                      {alert.type === 'deconnexion' && alert.dernier_contact && (
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          Dernier contact: {formatDate(alert.dernier_contact)}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2 flex-shrink-0">
                      {isAdmin && (
                        <Button
                          size="sm" variant="outline" className="h-7 rounded-full px-3 text-[11px]"
                          disabled={acquittement === alert.id}
                          onClick={() => basculerLu(alert)}
                          data-testid={`alert-lu-${alert.id}`}
                        >
                          {alert.lu
                            ? <><Undo2 className="w-3 h-3 mr-1" /> Marquer non lu</>
                            : <><Check className="w-3 h-3 mr-1" /> Marquer lu</>}
                        </Button>
                      )}
                      {ouvrable && (
                        <Button
                          size="sm" variant="outline" className="h-7 rounded-full px-3 text-[11px]"
                          onClick={() => navigate(`/clients/${alert.client_id}`)}
                          data-testid={`alert-fiche-${alert.id}`}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" /> Ouvrir la fiche
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Actions en masse — admin uniquement */}
      <BulkEditDialog
        open={bulkOpen}
        title="Édition groupée"
        noun="alerte"
        count={bulkSel.count}
        actions={ALERT_BULK_ACTIONS}
        selectedItems={bulkSel.selectedItems}
        busy={bulk.busy}
        onCancel={() => setBulkOpen(false)}
        onSubmit={runBulk}
      />

      <BulkResultDialog
        report={bulk.report} onClose={bulk.clearReport}
        title="Résultat de l'action en masse" resolveLabel={alertLabel}
      />
    </div>
  );
}
